/**
 * Routing and the app shell.
 *
 * Two gates: an unauthenticated visitor gets the login screen, and an authenticated one
 * without a chosen profile gets the profile picker. Both are convenience — the server
 * enforces the same rules on every request.
 */
import { Suspense, lazy } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSession } from './store/session';
import { AuthPage } from './pages/Auth';
import { ProfileSelectPage } from './pages/ProfileSelect';
import { ProfileSettingsPage } from './pages/ProfileSettings';
import { SourcesPage } from './pages/Sources';
import { LibraryPage } from './pages/Library';
import { SearchPage } from './pages/Search';
import { ItemDetailPage } from './pages/ItemDetail';
import { HomePage } from './pages/Home';
import { MyListPage } from './pages/MyList';
import { SeriesDetailPage } from './pages/SeriesDetail';
import { WatchPage } from './pages/Watch';
import { Spinner } from './components/ui';
import { Avatar } from './components/Avatar';
import { useDeviceProfile } from './device/useDeviceProfile';
import { useViewport } from './device/useViewport';
import { Icon, type IconName } from './components/Icon';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SearchOverlay } from './components/SearchOverlay';
import { TvShell } from './layouts/tv/TvShell';
import { TvHomePage } from './layouts/tv/pages/TvHome';
import { TvLibraryPage } from './layouts/tv/pages/TvLibrary';
import { TvSeriesDetailPage } from './layouts/tv/pages/TvSeriesDetail';
import { TvMyListPage } from './layouts/tv/pages/TvMyList';
import { TvItemDetailPage } from './layouts/tv/pages/TvItemDetail';
import { TvSearchPage } from './layouts/tv/pages/TvSearch';
import { TvPairPage } from './layouts/tv/pages/TvPair';
import { DevicesPage } from './pages/Devices';
import { PairApprovePage } from './pages/PairApprove';
import { RealtimeProvider } from './realtime/RealtimeContext';
import { RemotePage } from './pages/Remote';
import { useTvHandoff } from './realtime/useTvHandoff';
import { SettingsPage } from './pages/Settings';

/**
 * Picks the chrome.
 *
 * One route tree rather than two: the routes, the data hooks and the session are
 * identical on both devices, and duplicating them is how a fix lands on one and not the
 * other. Only the chrome and a handful of screens differ, and the screens differ by
 * element rather than by tree.
 */
function Shell({ children }: { children: React.ReactNode }) {
  const device = useDeviceProfile();
  if (device === 'tv') return <TvShell>{children}</TvShell>;
  return <WebShell>{children}</WebShell>;
}

/**
 * Chrome for the screens that come before a profile exists — login and the picker.
 *
 * On the TV these still need the focus provider or the remote does nothing at all, but
 * they must not get the sidebar: its links go to screens that require the profile the
 * user has not chosen yet.
 */
function PreShell({ children }: { children: React.ReactNode }) {
  const device = useDeviceProfile();
  if (device === 'tv') return <TvShell bare>{children}</TvShell>;
  return <>{children}</>;
}

/**
 * The six places browsing goes.
 *
 * Everything administrative moved behind `/settings`, because the bar had grown to ten
 * links and four of them were configuration — including one labelled "Kaynaklar" that led
 * straight to Xtream and container formats. Requirement 3 (extreme simplicity) and
 * requirement 16 (no IPTV terminology while browsing) are the same fix.
 *
 * One list, rendered twice: the top bar on a desktop and the thumb bar on a phone. Two
 * lists would be two places to add a section to, and the one somebody forgets is always
 * the phone.
 */
const PRIMARY_NAV = [
  /*
   * `short` is the thumb bar's label.
   *
   * Seven cells across a 390px phone leaves 47.7px of text per cell, and "Ana Sayfa"
   * measures 47.8px — it wrapped to two lines by a tenth of a pixel, which made the home
   * cell taller than the other six and pushed "Sayfa" against the bottom edge. On a 360px
   * phone the margin is 4px worse, so this is not a one-device rounding accident.
   *
   * A second nav list would be the obvious fix and the wrong one, for the reason above:
   * whichever list somebody forgets to update is always the phone's. One entry, two labels.
   */
  { to: '/home', label: 'Ana Sayfa', short: 'Ana', icon: 'home' },
  // Second, right after Home: it is the fastest route to a specific title.
  { to: '/search', label: 'Ara', icon: 'search' },
  { to: '/movies', label: 'Filmler', icon: 'movies' },
  { to: '/series', label: 'Diziler', icon: 'series' },
  { to: '/live', label: 'Canlı TV', icon: 'live' },
  { to: '/mylist', label: 'Listem', icon: 'plus' },
] as const satisfies readonly {
  to: string;
  label: string;
  short?: string;
  icon: IconName;
}[];

/**
 * The last cell, which is two different doors.
 *
 * For everybody else it is Settings — the avatar in the top bar and the cog in the thumb
 * bar both lead there, because on a phone there is no room for a switcher *and* a settings
 * link and they are the same idea to the person tapping.
 *
 * For a kid profile there is no Settings to lead to (§16: no Settings, no Sources, no
 * Devices), so the same cell becomes the profile switcher — which is the honest exit from
 * kids mode and the only one a child needs. Removing the cell instead would leave a child
 * with no way back to a grown-up's profile at all.
 */
function lastNavCell(kids: boolean): { to: string; label: string; icon: IconName } {
  return kids
    ? { to: '/profiles', label: 'Profil değiştir', icon: 'profiles' }
    : { to: '/settings', label: 'Ayarlar', icon: 'settings' };
}

function WebShell({ children }: { children: React.ReactNode }) {
  const { profile } = useSession();
  const location = useLocation();
  // The player is full-bleed; chrome would only get in the way. The search overlay goes
  // with the chrome — `/` during a film should not put a dialog over the picture, and the
  // player owns the keyboard while it is on screen.
  const bare = location.pathname.startsWith('/watch/');

  if (bare) return <>{children}</>;

  /*
   * Kids mode is a class on the shell, not a second shell.
   *
   * `patterns/kids.css` re-points the tokens underneath every component from here down —
   * accent, radii, card size — so the child gets the same app in a different language
   * rather than a parallel implementation that drifts. See that file for why.
   */
  const kids = Boolean(profile?.isKid);
  const last = lastNavCell(kids);

  return (
    <div className={`shell${kids ? ' kids' : ''}`}>
      <nav className="shell__nav" aria-label="Bölümler">
        <span className="shell__brand">Filmodasi</span>
        {PRIMARY_NAV.map((s) => (
          <NavLink key={s.to} to={s.to} className="shell__link">
            {/* The same icon the phone bar draws. A section that is a glyph on one device
                and a word on the other is two things to learn instead of one. */}
            <Icon name={s.icon} size={1.1} />
            {s.label}
          </NavLink>
        ))}
        <span className="shell__spacer" />
        {/* The avatar is the settings door as well as the profile switcher: on a phone
            there is no room for both, and they are the same idea to the person tapping. */}
        <NavLink to={last.to} className="shell__profile" aria-label={last.label}>
          {profile && <Avatar profile={profile} size={28} />}
          <span className="shell__profileName">{profile?.name ?? 'Profil'}</span>
        </NavLink>
      </nav>

      {children}

      {/*
        The phone's navigation, at the bottom where a thumb reaches.

        A top bar on a phone puts every section at the far end of a stretch, and on a
        six-inch screen that is the difference between a section people use and one they
        do not. Hidden on desktop by the stylesheet rather than by a second render path.
      */}
      <nav className="thumbbar" aria-label="Bölümler">
        {PRIMARY_NAV.map((s) => (
          <NavLink key={s.to} to={s.to} className="thumbbar__item" aria-label={s.label}>
            <span className="thumbbar__icon">
              <Icon name={s.icon} />
            </span>
            {/* `aria-label` above carries the full name for anyone who cannot see the
                icon; the abbreviation is only the drawn text. */}
            <span className="thumbbar__label" aria-hidden>
              {'short' in s ? s.short : s.label}
            </span>
          </NavLink>
        ))}
        <NavLink to={last.to} className="thumbbar__item" aria-label={last.label}>
          <span className="thumbbar__icon">
            <Icon name={last.icon} />
          </span>
          {/* "Profil değiştir" is two words too many for a 47px cell; the full name is on
              the `aria-label` above, as it is for "Ana Sayfa". */}
          <span className="thumbbar__label" aria-hidden>
            {kids ? 'Profil' : 'Ayarlar'}
          </span>
        </NavLink>
      </nav>

      {/* Renders nothing until `/` is pressed. Mounted here rather than per-page so the
          shortcut works on every screen that has chrome. */}
      <SearchOverlay />
    </div>
  );
}

/*
 * The motion lab, in development builds only.
 *
 * Written as a conditional expression rather than a conditional `<Route>` so that the
 * `import()` itself disappears from a production build. Vite replaces `import.meta.env.DEV`
 * with `false`, Rollup collapses `false ? lazy(...) : null` to `null`, and the dynamic
 * import goes with it — which is what keeps Three.js, its scene and the lab page out of
 * the deployed bundle and out of the Tizen widget entirely. A `lazy()` written at module
 * scope and merely *used* inside a dev branch would not be removed, and the widget would
 * quietly grow by six hundred kilobytes of code it is forbidden from running.
 */
const MotionLab = import.meta.env.DEV ? lazy(() => import('./pages/MotionLab')) : null;

/**
 * One connection for the whole app, above the routes so that switching screens does not
 * reconnect. Deliberately not per-screen: presence and "play on TV" have to work while the
 * phone is anywhere in the app, and the server replaces a device's existing connection
 * when a second one arrives — so per-screen sockets would evict each other on navigation.
 */
export function App() {
  const { user, profile } = useSession();
  const tv = useDeviceProfile() === 'tv';
  // The device profile already distinguishes a television from everything else; this is
  // the phone/desktop split, which is all a device label needs.
  //
  // Through the hook rather than an inline `matchMedia('(max-width: 820px)')`: that read
  // was a fourth copy of a breakpoint nothing else agreed with, and it sampled once at
  // mount, so a phone rotated into landscape kept reporting itself as a phone — or, on a
  // narrow desktop window, a desktop reported itself as a phone for the whole session.
  const viewport = useViewport();

  return (
    <RealtimeProvider
      enabled={Boolean(user)}
      profileId={profile?.id ?? null}
      deviceType={tv ? 'tv' : viewport === 'phone' ? 'phone' : 'desktop'}
    >
      <AppRoutes />
    </RealtimeProvider>
  );
}

function AppRoutes() {
  const { user, profile, loading } = useSession();
  const tv = useDeviceProfile() === 'tv';
  /*
   * Keys the error boundary below, so navigating away from a screen that threw clears the
   * failure. Without it the fallback would persist across the whole session — the boundary
   * has no way to know anything improved — and on a television, where there is no reload,
   * that means the app is gone until it is force-quit.
   */
  const { pathname } = useLocation();

  // Above the routes on purpose: the whole point is that the television is on some other
  // screen when the phone starts something. See useTvHandoff.
  useTvHandoff(tv && Boolean(profile));

  if (loading) return <Spinner label="Oturum kontrol ediliyor…" />;

  if (!user) {
    return (
      <PreShell>
        <Routes>
          <Route path="/register" element={<AuthPage mode="register" />} />
          {/*
            The television's way in, and on a TV it is the *only* one worth offering — so
            it is a route of its own rather than a mode of the login screen. The web keeps
            the password form: a phone already has a keyboard, and it is the phone that
            has to be signed in for pairing to mean anything.
          */}
          {tv && <Route path="/pair" element={<TvPairPage />} />}
          <Route path="*" element={<AuthPage mode="login" />} />
        </Routes>
      </PreShell>
    );
  }

  if (!profile) {
    return (
      <PreShell>
        <Routes>
          <Route path="/profiles" element={<ProfileSelectPage />} />
          {/*
            Outside the profile gate on purpose.

            Approving a pairing is an account-level action — it is the household vouching
            for a device — and the screen makes the person pick the TV's profile anyway.
            Behind the gate, somebody who scanned the QR before choosing a profile was
            bounced to the picker and the code in the URL was lost, which is precisely the
            moment the feature exists to serve.
          */}
          <Route path="/pair" element={<PairApprovePage />} />
          <Route path="*" element={<Navigate to="/profiles" replace />} />
        </Routes>
      </PreShell>
    );
  }

  return (
    <Shell>
      {/*
        Inside the shell, not around it.

        A crash in a screen must leave the navigation standing — that is the difference
        between "this page failed" and "the app is gone", and on the panel it is the
        difference between pressing Back and holding the power button. Wrapping the shell
        would take the nav down with the screen.
      */}
      <ErrorBoundary resetKey={pathname}>
        <Routes>
          <Route path="/profiles" element={<ProfileSelectPage />} />
          {/*
            Administration is not part of the kids app.

            §16: two levels of navigation, no Settings, no Sources, no Devices. Hiding the
            links alone would leave the URLs — and the profile settings screen in particular
            is where a child would find the switch that turns kids mode off.

            This is a redirect, not a lock. The lock is in `routes/profiles.ts`, which now
            refuses `{ isKid: false }` unless the request carries the account password —
            so a child reaching the API directly, from a console or a laptop, is refused
            too. Both are wanted: the redirect is what stops a child *finding* the switch,
            and the server rule is what stops them using it. Neither substitutes for the
            other, and the client half is the one that cannot be trusted.
          */}
          {!profile.isKid && (
            <Route path="/profiles/settings" element={<ProfileSettingsPage />} />
          )}
          <Route path="/home" element={tv ? <TvHomePage /> : <HomePage />} />
          <Route path="/mylist" element={tv ? <TvMyListPage /> : <MyListPage />} />
          {!profile.isKid && <Route path="/sources" element={<SourcesPage />} />}
          {!profile.isKid && <Route path="/settings" element={<SettingsPage />} />}
          {!profile.isKid && <Route path="/devices" element={<DevicesPage />} />}
          {/* The remote is a phone and desktop screen. A television controlling itself over
              a socket would be a longer path to the keys it already has. */}
          {!tv && <Route path="/remote" element={<RemotePage />} />}
          {/* The phone's approval screen. Authenticated by definition — the whole point is
              that a device which already belongs to the household vouches for a new one. */}
          <Route path="/pair" element={<PairApprovePage />} />
          <Route path="/search" element={tv ? <TvSearchPage /> : <SearchPage />} />
          {/*
            One component, three libraries. The segment is passed explicitly rather than
            read from a `:library` parameter, so an unknown value can never reach the API
            path — and React Router 7 has no regex path syntax to constrain it with.
  
            The TV gets a different component for the same route because its grid is
            windowed: a live library holds twenty thousand channels and the panel cannot
            hold that many nodes. Everything below the rendering — paging, filters, the API
            path — is the same hook.
          */}
          <Route
            path="/live"
            element={tv ? <TvLibraryPage library="live" /> : <LibraryPage library="live" />}
          />
          <Route
            path="/movies"
            element={tv ? <TvLibraryPage library="movies" /> : <LibraryPage library="movies" />}
          />
          <Route
            path="/series"
            element={tv ? <TvLibraryPage library="series" /> : <LibraryPage library="series" />}
          />
          <Route
            path="/series/:id"
            element={tv ? <TvSeriesDetailPage /> : <SeriesDetailPage />}
          />
          {/* The TV's item screen. The web has no equivalent — a pointer can hover a card
              for its title and click through in one motion, so an extra page between the
              grid and the film would only be in the way. */}
          {/* Both layouts have a real detail screen now. The web arm was a redirect
              straight to the player, which is why a card was a play button rather than a
              way in. */}
          <Route path="/item/:id" element={tv ? <TvItemDetailPage /> : <ItemDetailPage />} />
          <Route path="/watch/:id" element={<WatchPage />} />
          {MotionLab && (
            <Route
              path="/motion-lab"
              element={
                <Suspense fallback={<Spinner />}>
                  <MotionLab />
                </Suspense>
              }
            />
          )}
          {/* The homepage, not Live TV: it is the one screen that knows what this
              profile was in the middle of. */}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </ErrorBoundary>
    </Shell>
  );
}
